/**
 * Reads GET /v1/core/directory, which serves the `employee` table loaded from
 * Rippling. That source is what lets store staff appear at all: most clerks
 * share one Google account per store, so the previous Microsoft and Google merge
 * could not see any of them, and this page showed only corporate people to an
 * audience that is almost entirely store staff.
 *
 * A 404 or a 503 resolves to an empty roster rather than throwing, so a store
 * tablet on a stage where the roster is not loaded shows an empty-state card
 * instead of an error.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { DirectoryPerson, DirectoryStore } from '@ampersandbrands/ui';
import { apiGet, type ApiError } from './api';
import { API_BASE } from './apiBase';

export type { DirectoryPerson, DirectoryStore };

export type DirectoryResponse = {
  people: DirectoryPerson[];
  stores: DirectoryStore[];
  corporate_phone: string | null;
  synced_at: string | null;
};

const EMPTY: DirectoryResponse = {
  people: [],
  stores: [],
  corporate_phone: null,
  synced_at: null,
};

export function useDirectory(): UseQueryResult<DirectoryResponse, Error> {
  return useQuery<DirectoryResponse, Error>({
    queryKey: ['core', 'directory'],
    queryFn: async () => {
      try {
        return await apiGet<DirectoryResponse>(`${API_BASE}/v1/core/directory`);
      } catch (err) {
        const status = (err as ApiError).status;
        if (status === 404 || status === 503) {
          return EMPTY;
        }
        throw err;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}
